// 報酬・アフィリ料率（design/README.md §5）。
// 料率の正本は affiliate-harness の commission_rate（クリエイター単位）。案件は基本料率 base_rate だけを持つ（plan.md §0-3）。
import { useEffect, useMemo, useState } from "react";
import type {
  AffAffiliate,
  GfCampaign,
  GfCampaignType,
  GfChannels,
  GfCreator,
  GfProductMode,
  GfSetProduct,
  GfTabProps,
  Loadable,
} from "../../../types/gifting";
import { createAffAffiliate, fetchGfCatalogSets, saveAffRate, saveGfCampaign } from "../../../api/giftingClient";
import {
  CAMPAIGN_TYPE_LABEL,
  CAMPAIGN_TYPES,
  DEFAULT_REUSE_DM_TEMPLATE_ID,
  estimatePayout,
  fmtPct,
  fmtYen,
  handleOf,
  isArchived,
  primarySns,
  resolveRate,
  typeHasAffiliate,
  typeHasFee,
} from "../../../lib/giftingRules";
import { GfButton, GfCard, GfEmpty, GfErrorBanner, GfSeg, GfSkeleton, GfTag, type GfTone } from "../ui/GfUi";
import { ReuseDmSetting } from "../modals/ReuseDmSetting";
import { ConfirmModal } from "./kit/ConfirmModal";
import { affMapOf, creatorMap, errMsg } from "./kit/helpers";

/** 試算用の想定売上（実績ではない） */
const SIM_SALES = 200_000;

const CHANNEL_DEFS: Array<{ key: keyof GfChannels; label: string; connected: boolean }> = [
  { key: "shopify", label: "Shopify", connected: true },
  { key: "rakuten", label: "楽天", connected: false },
  { key: "amazon", label: "Amazon", connected: false },
];

const SRC_TONE: Record<string, GfTone> = { 個別: "ok", 案件: "muted", 未登録: "caution", アフィリなし: "plain" };

const toPct = (r: number | null) => Math.round((r ?? 0) * 100);

export function RatesTab(props: GfTabProps) {
  const { data, nav } = props;
  const campaigns = data.board.campaigns.filter((c) => c.status === "active" || c.id === nav.state.rateCampaign);
  const camp = campaigns.find((c) => c.id === nav.state.rateCampaign) ?? campaigns[0] ?? null;

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">報酬・アフィリ料率</h1>
        <div className="gf-rate-tabs">
          {campaigns.map((c) => (
            <button
              type="button"
              key={c.id}
              className={`gf-rate-tab${camp?.id === c.id ? " is-active" : ""}`}
              onClick={() => nav.set({ rateCampaign: c.id })}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
      {camp ? (
        // 保存後（updated_at が変わる）や案件切替でフォームを初期化し直す
        <RatesEditor key={`${camp.id}:${camp.updated_at}`} camp={camp} {...props} />
      ) : (
        <GfCard>
          <GfEmpty>案件がありません。案件タブの「新規案件」から作成してください。</GfEmpty>
        </GfCard>
      )}
    </>
  );
}

function RatesEditor({ camp, data, refresh, toast }: GfTabProps & { camp: GfCampaign }) {
  const [type, setType] = useState<GfCampaignType>(camp.type);
  const [feeStr, setFeeStr] = useState(String(camp.fee_amount ?? 0));
  const [basePct, setBasePct] = useState(toPct(camp.base_rate));
  const [channels, setChannels] = useState<GfChannels>(camp.channels);
  const [reuse, setReuse] = useState<"1" | "0">(camp.reuse_required === 0 ? "0" : "1");
  // 第3弾: 投稿確認のあと2次利用の依頼DMを自動で送る（null=既定「ご投稿のお礼」）
  const savedDmTpl = camp.reuse_dm_template_id === DEFAULT_REUSE_DM_TEMPLATE_ID ? null : camp.reuse_dm_template_id;
  const [reuseDmAuto, setReuseDmAuto] = useState(camp.reuse_dm_auto === 1);
  const [reuseDmTpl, setReuseDmTpl] = useState<string | null>(savedDmTpl);
  // 送る商品・SKU（エントリー作成時の既定値。第2弾の出荷指示は SKU 必須）
  const [productName, setProductName] = useState(camp.product_name ?? "");
  const [sku, setSku] = useState(camp.sku ?? "");
  // 「クリエイターが選ぶ」= 本人フォームでセットを選ぶ（2026-10-06〜の標準）
  const [productMode, setProductMode] = useState<GfProductMode>(camp.product_mode);
  const savedOptionSkus = camp.product_options.map((p) => p.sku);
  const [optionSkus, setOptionSkus] = useState<string[]>(savedOptionSkus);
  const [catalog, setCatalog] = useState<Loadable<GfSetProduct[]>>({ status: "loading" });
  useEffect(() => {
    let live = true;
    fetchGfCatalogSets()
      .then((d) => {
        if (!live) return;
        setCatalog({ status: "ok", data: d });
        // 選択肢が未保存（fixed からの切替）のときは全セットを既定にする
        setOptionSkus((cur) => (cur.length ? cur : d.map((p) => p.sku)));
      })
      .catch((e: unknown) => live && setCatalog({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, []);
  const [saving, setSaving] = useState(false);

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [regTarget, setRegTarget] = useState<GfCreator | null>(null);
  const [regBusy, setRegBusy] = useState(false);

  const creators = useMemo(() => creatorMap(data.board.creators), [data.board.creators]);
  const affs = affMapOf(data.affiliates);
  const fee = Number(feeStr) || 0;

  const dirty =
    type !== camp.type ||
    (typeHasFee(type) && fee !== (camp.fee_amount ?? 0)) ||
    (typeHasAffiliate(type) && basePct !== toPct(camp.base_rate)) ||
    CHANNEL_DEFS.some((c) => channels[c.key] !== camp.channels[c.key]) ||
    (reuse === "1" ? 1 : 0) !== camp.reuse_required ||
    reuseDmAuto !== (camp.reuse_dm_auto === 1) ||
    reuseDmTpl !== savedDmTpl ||
    productMode !== camp.product_mode ||
    (productMode === "fixed" && (productName.trim() !== (camp.product_name ?? "") || sku.trim() !== (camp.sku ?? ""))) ||
    (productMode === "choice" && [...optionSkus].sort().join() !== [...savedOptionSkus].sort().join());

  const save = async () => {
    if (productMode === "choice" && optionSkus.length === 0) return toast("クリエイターが選べるセットを1つ以上チェックしてください");
    setSaving(true);
    try {
      await saveGfCampaign(camp.id, {
        product_mode: productMode,
        ...(productMode === "fixed"
          ? { product_name: productName.trim() || null, sku: sku.trim() || null }
          : { product_options: optionSkus }),
        type,
        fee_amount: typeHasFee(type) ? fee : null,
        base_rate: typeHasAffiliate(type) ? basePct / 100 : null,
        channels,
        reuse_required: reuse === "1" ? 1 : 0,
        // 第3弾の項目は変えたときだけ送る（旧バックエンドに余計なキーを渡さない）
        ...(reuseDmAuto !== (camp.reuse_dm_auto === 1) ? { reuse_dm_auto: reuseDmAuto ? (1 as const) : (0 as const) } : {}),
        ...(reuseDmTpl !== savedDmTpl ? { reuse_dm_template_id: reuseDmTpl } : {}),
      });
      toast("案件の設定を保存しました");
      await refresh();
    } catch (e) {
      toast(`保存できませんでした: ${errMsg(e)}`);
    } finally {
      setSaving(false);
    }
  };

  // ---- 試算（編集中の値で即時再計算） ----
  const simPay = estimatePayout(typeHasFee(type) ? fee : 0, typeHasAffiliate(type) ? basePct / 100 : null, SIM_SALES);

  // ---- クリエイター別（保存済みの案件設定で計算） ----
  const memberIds = [
    ...new Set(
      data.board.entries
        .filter((e) => e.campaign_id === camp.id && !isArchived(e))
        .map((e) => e.influencer_id)
    ),
  ];
  const members = memberIds.map((id) => creators.get(id)).filter((c): c is GfCreator => !!c);
  const savedFee = typeHasFee(camp.type) ? camp.fee_amount ?? 0 : 0;
  const hasAff = typeHasAffiliate(camp.type);
  const base = camp.base_rate ?? 0;

  const serverOverride = (a: AffAffiliate | undefined): string =>
    a && Math.abs(a.commission_rate - base) > 1e-9 ? String(Math.round(a.commission_rate * 1000) / 10) : "";

  const rows = members.map((c) => {
    const a = affs.get(c.id);
    const rr = resolveRate(a, camp);
    return { c, a, rr, pay: estimatePayout(savedFee, rr.rate, SIM_SALES) };
  });
  const total = rows.reduce((s, r) => s + r.pay, 0);

  const commit = async (creatorId: string) => {
    const a = affs.get(creatorId);
    const txt = drafts[creatorId];
    if (!a || txt === undefined) return;
    let next: number;
    if (txt.trim() === "") next = base;
    else {
      const v = Number(txt);
      if (!Number.isFinite(v) || v < 0 || v > 100) {
        toast("個別料率は 0〜100 の数字（%）で入力してください");
        return;
      }
      next = Math.round(v * 10) / 1000;
    }
    const clearDraft = () =>
      setDrafts((d) => {
        const { [creatorId]: _removed, ...rest } = d;
        return rest;
      });
    if (Math.abs(next - a.commission_rate) < 1e-9) return clearDraft();
    setBusyId(creatorId);
    try {
      await saveAffRate(a.id, next);
      toast(txt.trim() === "" ? "案件の基本料率に戻しました" : "個別料率を保存しました");
      clearDraft();
      await refresh();
    } catch (e) {
      toast(`保存できませんでした: ${errMsg(e)}`);
    } finally {
      setBusyId(null);
    }
  };

  const register = async () => {
    if (!regTarget) return;
    setRegBusy(true);
    try {
      const res = await createAffAffiliate({
        name: regTarget.name,
        type: "influencer",
        gifting_influencer_id: regTarget.id,
        commission_rate: base,
      });
      toast(`アフィリエイトを登録しました（クーポン ${res.coupon_code}）`);
      setRegTarget(null);
      await refresh();
    } catch (e) {
      toast(`登録できませんでした: ${errMsg(e)}`);
    } finally {
      setRegBusy(false);
    }
  };

  return (
    <>
      <div className="gf-grid-2 is-320 is-start">
        {/* 案件設定 */}
        <GfCard className="gf-c20 gf-settings-card">
          <div className="gf-setting">
            <span className="gf-setting-label">送る商品</span>
            <GfSeg<GfProductMode>
              ariaLabel="送る商品の決め方"
              value={productMode}
              onChange={setProductMode}
              options={[
                { value: "choice", label: "クリエイターが選ぶ" },
                { value: "fixed", label: "固定の商品" },
              ]}
            />
            {productMode === "choice" ? (
              <>
                <span className="gf-note">本人フォームで、チェックしたセットから1つ選んでもらいます（価格は表示しません）。</span>
                {catalog.status === "loading" && <GfSkeleton height={120} />}
                {catalog.status === "error" && <span className="gf-note gf-warn-text">セット一覧を取得できませんでした（{catalog.message}）</span>}
                {catalog.status === "ok" && (
                  <div className="gf-set-options">
                    {catalog.data.map((p) => (
                      <label key={p.sku} className="gf-set-option">
                        <input
                          type="checkbox"
                          checked={optionSkus.includes(p.sku)}
                          onChange={(e) =>
                            setOptionSkus((cur) => (e.target.checked ? [...cur, p.sku] : cur.filter((s) => s !== p.sku)))
                          }
                        />
                        <span className="gf-set-option-text">
                          <span className="gf-set-option-name">{p.name}</span>
                          <span className="gf-note">{p.contents}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <input
                  className="gf-input"
                  value={productName}
                  maxLength={200}
                  aria-label="送る商品の名前"
                  placeholder="例）4サイズスターターセット"
                  onChange={(e) => setProductName(e.target.value)}
                />
                {!productName.trim() && <span className="gf-note gf-warn-text">未設定だと、この案件にクリエイターを追加できません</span>}
                <span className="gf-setting-label">SKU（倉庫の商品コード）</span>
                <input className="gf-input is-mono" value={sku} maxLength={64} placeholder="例）STQ-SET-4SIZE-WH" onChange={(e) => setSku(e.target.value)} />
                {!sku.trim() && <span className="gf-note">未設定だと「出荷指示を送る」が使えません（手入力の発送は可）</span>}
              </>
            )}
          </div>
          <div className="gf-setting">
            <span className="gf-setting-label">案件タイプ</span>
            <div className="gf-type-seg" role="tablist" aria-label="案件タイプ">
              {CAMPAIGN_TYPES.map((t) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={type === t}
                  key={t}
                  className={`gf-type-btn${type === t ? " is-active" : ""}`}
                  onClick={() => setType(t)}
                >
                  {CAMPAIGN_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          {typeHasFee(type) && (
            <label className="gf-setting">
              <span className="gf-setting-label">固定報酬（税抜・1名あたり）</span>
              <span className="gf-input-affix">
                <span className="gf-muted">¥</span>
                <input inputMode="numeric" value={feeStr} onChange={(e) => setFeeStr(e.target.value.replace(/[^0-9]/g, ""))} />
              </span>
            </label>
          )}

          {typeHasAffiliate(type) && (
            <>
              <div className="gf-setting">
                <div className="gf-setting-row">
                  <span className="gf-setting-label">案件の基本アフィリ料率</span>
                  <span className="gf-mono gf-setting-value">{basePct}%</span>
                </div>
                <input
                  type="range"
                  className="gf-slider"
                  min={0}
                  max={30}
                  step={1}
                  value={basePct}
                  aria-label="案件の基本アフィリ料率"
                  onChange={(e) => setBasePct(Number(e.target.value))}
                />
                <span className="gf-note gf-lh">個別料率が設定されたクリエイターは、そちらが優先されます。</span>
              </div>
              <div className="gf-setting">
                <span className="gf-setting-label">計測対象の販売チャネル</span>
                <div className="gf-chips">
                  {CHANNEL_DEFS.map((c) => (
                    <button
                      type="button"
                      key={c.key}
                      aria-pressed={channels[c.key]}
                      className={`gf-rate-chip${channels[c.key] ? " is-active" : ""}`}
                      onClick={() => setChannels((ch) => ({ ...ch, [c.key]: !ch[c.key] }))}
                    >
                      {channels[c.key] ? "✓ " : ""}
                      {c.label}
                    </button>
                  ))}
                </div>
                <span className="gf-note gf-lh">楽天・Amazon は売上計測が未接続です（選択しても現在は売上に反映されません）。</span>
              </div>
            </>
          )}

          <div className="gf-setting">
            <span className="gf-setting-label">2次利用</span>
            <GfSeg<"1" | "0">
              ariaLabel="2次利用の有無"
              value={reuse}
              onChange={setReuse}
              options={[
                { value: "1", label: "2次利用あり" },
                { value: "0", label: "2次利用なし" },
              ]}
            />
            {reuse === "0" && <span className="gf-note gf-lh">2次利用なしの案件は、投稿確認から直接アーカイブされます。</span>}
          </div>
          {reuse === "1" && <ReuseDmSetting auto={reuseDmAuto} templateId={reuseDmTpl} onAuto={setReuseDmAuto} onTemplate={setReuseDmTpl} />}

          <div className="gf-boxed gf-sim">
            <div className="gf-box-row gf-note">
              <span>試算：1名あたり想定売上（想定）</span>
              <span className="gf-mono gf-ink">{fmtYen(SIM_SALES)}</span>
            </div>
            <div className="gf-box-row">
              <span>支払見込（基本料率時）</span>
              <span className="gf-mono gf-sim-pay">{fmtYen(simPay)}</span>
            </div>
            <span className="gf-note gf-lh">想定売上は試算用の仮の値で、実績ではありません。</span>
          </div>

          <div className="gf-save-row">
            {dirty && <span className="gf-note">未保存の変更があります</span>}
            <GfButton variant="primary" disabled={!dirty || saving} onClick={() => void save()}>
              {saving ? "保存中…" : "保存"}
            </GfButton>
          </div>
        </GfCard>

        {/* クリエイター別 */}
        <GfCard flush>
          <div className="gf-card-pad-head">
            <h2 className="gf-h2">クリエイター別の適用料率</h2>
            <span className="gf-note">空欄＝案件料率（保存済みの設定で計算）</span>
          </div>
          {data.affiliates.status === "error" && (
            <div className="gf-pad-x">
              <GfErrorBanner message={`アフィリ情報を取得できませんでした（${data.affiliates.message}）`} onRetry={() => void refresh()} />
            </div>
          )}
          {data.affiliates.status === "loading" && (
            <div className="gf-pad-x">
              <GfSkeleton height={36} />
            </div>
          )}
          <div className="gf-table-wrap">
            <div className="gf-rate-table">
              <div className="gf-rate-row is-head">
                <span>名前</span>
                <span>個別料率</span>
                <span>適用</span>
                <span className="is-right">支払見込</span>
              </div>
              {rows.map(({ c, a, rr, pay }) => {
                const shown = drafts[c.id] ?? serverOverride(a);
                return (
                  <div className="gf-rate-row" key={c.id}>
                    <span className="gf-name-cell-text">
                      <span className="gf-name-strong">{c.name}</span>
                      <span className="gf-handle">{handleOf(primarySns(c.sns)) || "—"}</span>
                    </span>
                    <span>
                      {!hasAff ? (
                        <span className="gf-faint">—</span>
                      ) : a ? (
                        <span className="gf-input-affix is-sm">
                          <input
                            aria-label={`${c.name} の個別料率（%）`}
                            placeholder="—"
                            inputMode="decimal"
                            disabled={busyId === c.id}
                            value={shown}
                            onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value.replace(/[^0-9.]/g, "") }))}
                            onBlur={() => void commit(c.id)}
                            onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                          />
                          <span className="gf-muted">%</span>
                        </span>
                      ) : data.affiliates.status === "ok" ? (
                        <button type="button" className="gf-link-btn gf-small" onClick={() => setRegTarget(c)}>
                          登録
                        </button>
                      ) : (
                        <span className="gf-faint">…</span>
                      )}
                    </span>
                    <span className="gf-name-cell-text">
                      <span className="gf-mono">{rr.label}</span>
                      {hasAff && <GfTag tone={SRC_TONE[rr.src] ?? "plain"}>{rr.src}</GfTag>}
                    </span>
                    <span className="gf-mono is-right">{fmtYen(pay)}</span>
                  </div>
                );
              })}
              {rows.length === 0 && <GfEmpty>この案件に進行中のクリエイターはいません</GfEmpty>}
              <div className="gf-rate-foot">
                <span>合計支払見込（想定売上 {fmtYen(SIM_SALES)} / 1名）</span>
                <span className="gf-mono gf-strong">{fmtYen(total)}</span>
              </div>
            </div>
          </div>
          {hasAff && (
            <div className="gf-pad-x gf-pad-b">
              <span className="gf-note gf-lh">
                基本料率は新しく登録するクリエイターの初期値です。登録済みの方の料率は各行で変更します（空欄にして保存すると案件の基本料率に揃います）。
                「未登録」の方はアフィリ登録（Shopifyクーポン発行）が済むまで成果が計測されません。
              </span>
            </div>
          )}
        </GfCard>
      </div>

      <ConfirmModal
        open={regTarget !== null}
        title="アフィリエイトを登録"
        confirmLabel="登録する"
        busy={regBusy}
        onConfirm={() => void register()}
        onClose={() => setRegTarget(null)}
      >
        <p>
          {regTarget?.name} のアフィリエイト登録を行い、Shopify のクーポンを発行します。料率は案件の基本料率 {fmtPct(base)} で作成します。
        </p>
        <p className="gf-note">発行したクーポンは取り消せません。よろしいですか。</p>
      </ConfirmModal>
    </>
  );
}
